class AATaskGeneratorWorkspace {
  constructor(root) {
    this.root = root;
    this.currentCatalog = null;
    this.currentBatch = null;
    this.statusRefreshTimer = null;
    this.statusRefreshing = false;
    this.undoConfirmBatchId = null;
    this.generationPollToken = 0;

    this.bindEvents();
    this.initSettings();
    this.observeCatalogRows();

    window.setTimeout(
      () => this.refreshCatalogStatuses(),
      450
    );
  }

  escapeHtml(value = '') {
    return String(value)
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');
  }

  formatMonth(value = '') {
    const raw = String(value || '').slice(0, 7);

    if (!/^\d{4}-\d{2}$/.test(raw)) {
      return value || 'Catalog';
    }

    const [year, month] = raw
      .split('-')
      .map(Number);

    return new Intl.DateTimeFormat(
      'en-US',
      {
        month: 'long',
        year: 'numeric',
        timeZone: 'UTC'
      }
    ).format(
      new Date(
        Date.UTC(
          year,
          month - 1,
          1
        )
      )
    );
  }

  formatDate(value = '') {
    if (!value) {
      return 'Flexible';
    }

    const parts =
      String(value).split('-');

    if (parts.length !== 3) {
      return value;
    }

    return new Intl.DateTimeFormat(
      'en-US',
      {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        timeZone: 'UTC'
      }
    ).format(
      new Date(
        Date.UTC(
          Number(parts[0]),
          Number(parts[1]) - 1,
          Number(parts[2])
        )
      )
    );
  }

  getPanelHost() {
    return this.root.querySelector(
      '[data-aa-task-generator-panel]'
    );
  }

  showPanelMessage(
    message,
    tone = ''
  ) {
    const host =
      this.getPanelHost();

    if (!host) {
      return;
    }

    host.innerHTML = `
      <section
        class="aa-task-generator-panel
          ${tone ? `is-${this.escapeHtml(tone)}` : ''}"
      >
        <div class="aa-task-generator-panel__header">
          <div>
            <p class="aa-card__label">
              Task Generator
            </p>
            <h3>
              ${this.escapeHtml(message)}
            </h3>
          </div>

          <button
            type="button"
            class="aa-button"
            data-aa-close-task-generator
          >
            Close
          </button>
        </div>
      </section>
    `;
  }

  observeCatalogRows() {
    const clientRoot =
      this.root.querySelector(
        '[data-aa-clients-list]'
      );

    if (!clientRoot) {
      return;
    }

    this.catalogObserver =
      new MutationObserver(
        (mutations) => {
          const addedGeneratorUi =
            mutations.some(
              (mutation) =>
                Array.from(
                  mutation.addedNodes || []
                ).some((node) => {
                  if (
                    !(node instanceof Element)
                  ) {
                    return false;
                  }

                  return (
                    node.matches?.(
                      '[data-aa-generate-catalog-tasks]'
                    ) ||
                    Boolean(
                      node.querySelector?.(
                        '[data-aa-generate-catalog-tasks]'
                      )
                    )
                  );
                })
            );

          if (addedGeneratorUi) {
            this.scheduleCatalogStatusRefresh();
          }
        }
      );

    this.catalogObserver.observe(
      clientRoot,
      {
        childList: true,
        subtree: true
      }
    );
  }

  scheduleCatalogStatusRefresh() {
    window.clearTimeout(
      this.statusRefreshTimer
    );

    this.statusRefreshTimer =
      window.setTimeout(
        () =>
          this.refreshCatalogStatuses(),
        160
      );
  }

  async refreshCatalogStatuses() {
    if (
      this.statusRefreshing ||
      !window.AASupabaseClient
        ?.getTaskGenerationBatches
    ) {
      return;
    }

    const buttons =
      Array.from(
        this.root.querySelectorAll(
          '[data-aa-generate-catalog-tasks]'
        )
      );

    if (!buttons.length) {
      return;
    }

    const clientId =
      buttons[0].dataset
        .aaGeneratorClientId ||
      '';

    if (!clientId) {
      return;
    }

    this.statusRefreshing = true;

    try {
      const batches =
        await window.AASupabaseClient
          .getTaskGenerationBatches({
            clientId
          });

      const latestByCatalog =
        new Map();

      (batches || []).forEach(
        (batch) => {
          const key =
            String(
              batch.catalog_id ||
              ''
            );

          if (
            key &&
            !latestByCatalog.has(key)
          ) {
            latestByCatalog.set(
              key,
              batch
            );
          }
        }
      );

      buttons.forEach((button) => {
        const catalogId =
          button.dataset
            .aaGenerateCatalogTasks ||
          '';

        const badge =
          this.root.querySelector(
            `[data-aa-task-generation-status="${CSS.escape(catalogId)}"]`
          );

        if (!badge) {
          return;
        }

        const batch =
          latestByCatalog.get(
            String(catalogId)
          );

        badge.className =
          'aa-task-generation-status';

        if (!batch) {
          badge.textContent =
            'Not generated';
          return;
        }

        const labels = {
          generating: 'Leo is working',
          draft: 'Ready for review',
          approved: 'Tasks approved',
          undone: 'Undone',
          failed: 'Needs attention'
        };

        badge.textContent =
          labels[batch.status] ||
          'Not generated';

        badge.classList.add(
          `is-${String(
            batch.status ||
            'none'
          )}`
        );
      });
    } catch (error) {
      console.warn(
        'Unable to load Task Generator status:',
        error
      );
    } finally {
      this.statusRefreshing = false;
    }
  }

  async initSettings() {
    const host =
      this.root.querySelector(
        '[data-aa-task-generator-settings]'
      );

    if (!host) {
      return;
    }

    host.innerHTML = `
      <div class="aa-task-generator-settings__loading">
        Loading Task Generator Instructions...
      </div>
    `;

    try {
      const [
        settings,
        status
      ] = await Promise.all([
        window.AASupabaseClient
          .getTaskGeneratorSettings(),
        window.AASupabaseClient
          .getTaskGeneratorStatus()
          .catch(() => ({
            openaiConfigured: null,
            statusCheckFailed: true
          }))
      ]);

      this.renderSettings(
        host,
        settings,
        status
      );
    } catch (error) {
      console.error(
        'Unable to load Task Generator settings:',
        error
      );

      host.innerHTML = `
        <div class="aa-task-generator-settings__error">
          Unable to load Task Generator Instructions.
        </div>
      `;
    }
  }

  renderSettings(
    host,
    settings,
    status = {}
  ) {
    const connectionState =
      status.openaiConfigured === true
        ? 'ready'
        : status.openaiConfigured === false
          ? 'missing'
          : 'unknown';

    const connectionLabel = {
      ready: 'Leo plugged in',
      missing: 'Leo connection needs setup',
      unknown: 'Leo connection unavailable'
    }[connectionState];

    const connection = document.querySelector('[data-aa-leo-connection]');
    if (connection) {
      connection.textContent = connectionLabel;
      connection.dataset.state = connectionState;
      connection.title = connectionState === 'ready'
        ? 'Leo is connected to the task generation service.'
        : 'Task generation connection could not be confirmed.';
    }

    host.innerHTML = `
      <form
        class="aa-task-generator-settings-form"
        data-aa-task-generator-settings-form
      >
        <label>
          <span>
            Global Generator Prompt
          </span>

          <textarea
            data-aa-task-generator-instructions
            rows="24"
          >${this.escapeHtml(
            settings?.instructions ||
            ''
          )}</textarea>
        </label>

        <div class="aa-task-generator-settings__footer">
          <p>
            Change this whenever a test teaches us something.
            No code change is required.
          </p>

          <button
            type="submit"
            class="aa-button aa-button--primary"
          >
            Save Instructions
          </button>
        </div>

        <p
          class="aa-task-generator-feedback"
          data-aa-task-generator-settings-feedback
        ></p>
      </form>
    `;
  }

  async openCatalogGenerator(button) {
    const catalogId =
      button.dataset
        .aaGenerateCatalogTasks ||
      '';

    if (!catalogId) {
      return;
    }

    this.currentCatalog = {
      id: catalogId,
      clientId:
        button.dataset
          .aaGeneratorClientId ||
        '',
      clientName:
        button.dataset
          .aaGeneratorClientName ||
        'Client',
      month:
        button.dataset
          .aaGeneratorCatalogMonth ||
        '',
      title:
        button.dataset
          .aaGeneratorCatalogTitle ||
        'Catalog'
    };

    this.currentBatch = null;
    this.undoConfirmBatchId = null;

    this.showPanelMessage(
      'Loading task planning workspace...'
    );

    const host =
      this.getPanelHost();

    host?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });

    try {
      const latest =
        await window.AASupabaseClient
          .getLatestTaskGenerationBatch({
            catalogId
          });

      if (latest?.id) {
        this.currentBatch =
          await window.AASupabaseClient
            .getTaskGenerationBatch({
              id: latest.id
            });

        this.renderCurrentPanel();

        if (
          this.currentBatch.status ===
          'generating'
        ) {
          void this
            .resumeCurrentGeneration();
        }
      } else {
        this.renderStartPanel();
      }
    } catch (error) {
      console.error(
        'Unable to open Task Generator:',
        error
      );

      this.showPanelMessage(
        error?.message ||
        'Unable to open Task Generator.',
        'error'
      );
    }
  }

  async resumeCurrentGeneration() {
    if (
      !this.currentBatch?.id ||
      this.currentBatch.status !==
        'generating' ||
      !window.AASupabaseClient
        ?.pollTaskGeneratorBatch
    ) {
      return;
    }

    const batchId =
      this.currentBatch.id;
    const pollToken =
      ++this.generationPollToken;

    for (
      let attempt = 0;
      attempt < 135;
      attempt += 1
    ) {
      if (
        pollToken !==
        this.generationPollToken
      ) {
        return;
      }

      try {
        const batch =
          await window.AASupabaseClient
            .pollTaskGeneratorBatch({
              batchId
            });

        if (
          pollToken !==
          this.generationPollToken
        ) {
          return;
        }

        if (batch?.id) {
          this.currentBatch =
            batch;
          this.renderCurrentPanel();
          await this
            .refreshCatalogStatuses();
        }

        if (
          !batch ||
          batch.status !==
            'generating'
        ) {
          return;
        }
      } catch (error) {
        console.error(
          'Unable to resume Leo generation:',
          error
        );

        if (
          pollToken ===
          this.generationPollToken
        ) {
          this.setFeedback(
            error?.message ||
            'Leo could not refresh this generation.',
            'error'
          );
        }

        return;
      }

      await new Promise(
        (resolve) =>
          window.setTimeout(
            resolve,
            5000
          )
      );
    }

    if (
      pollToken ===
      this.generationPollToken
    ) {
      this.setFeedback(
        'Leo is still working. You can leave this page and check again later.'
      );
    }
  }

  renderStartPanel() {
    const host =
      this.getPanelHost();

    if (
      !host ||
      !this.currentCatalog
    ) {
      return;
    }

    const catalog =
      this.currentCatalog;

    host.innerHTML = `
      <section class="aa-task-generator-panel">
        <div class="aa-task-generator-panel__header">
          <div>
            <p class="aa-card__label">
              Generate Tasks
            </p>
            <h3>
              ${this.escapeHtml(
                catalog.clientName
              )}
              ·
              ${this.escapeHtml(
                this.formatMonth(
                  catalog.month
                )
              )}
            </h3>
            <p>
              ${this.escapeHtml(
                catalog.title
              )}
            </p>
          </div>

          <button
            type="button"
            class="aa-button"
            data-aa-close-task-generator
          >
            Close
          </button>
        </div>

        <div class="aa-task-generator-context-note">
          <strong>
            The Workspace already supplies the structure.
          </strong>
          <span>
            The stored catalog PDF, Marketing Package,
            Production Rules, Task Anchors, existing monthly
            tasks, and global generator instructions are read
            automatically.
          </span>
        </div>

        <label class="aa-task-generator-monthly-notes">
          <span>
            Anything the system needs to know specifically
            about this month?
          </span>

          <textarea
            rows="7"
            data-aa-task-generator-monthly-notes
            placeholder="Optional. Example: Already covered: catalog, Layer Cake sale. Prioritize: October promotions and early November classes. Avoid: repeating the same manufacturer offer."
          ></textarea>
        </label>

        <div class="aa-task-generator-panel__actions">
          <button
            type="button"
            class="aa-button aa-button--primary"
            data-aa-run-task-generator
          >
            Generate Proposal
          </button>
        </div>

        <p
          class="aa-task-generator-feedback"
          data-aa-task-generator-feedback
        ></p>
      </section>
    `;
  }

  getSlotLabel(item) {
    const labels = {
      fixed_anchor: 'Fixed Anchor',
      conditional_anchor:
        'Conditional Anchor',
      flexible_anchor:
        'Flexible Anchor',
      flexible_package:
        'Package Slot'
    };

    return (
      labels[item.slot_kind] ||
      'Task'
    );
  }

  renderTaskGroup(
    taskType,
    items,
    editable
  ) {
    return `
      <details
        class="aa-task-generator-group"
        open
      >
        <summary>
          <span>
            ${this.escapeHtml(taskType)}
          </span>
          <strong>
            ${items.filter(
              (item) =>
                item.included !== false
            ).length}
            planned
          </strong>
        </summary>

        <div class="aa-task-generator-group__rows">
          ${items
            .map(
              (item) =>
                this.renderTaskRow(
                  item,
                  editable
                )
            )
            .join('')}
        </div>
      </details>
    `;
  }

  renderTaskRow(
    item,
    editable
  ) {
    const metadata =
      item.metadata &&
      typeof item.metadata ===
        'object'
        ? item.metadata
        : {};

    const fixedDate =
      metadata.fixedDate ||
      '';

    const allowedMin =
      metadata.allowedDateMin ||
      '';

    const allowedMax =
      metadata.allowedDateMax ||
      '';

    const packageLabel =
      item.counts_toward_package
        ? (
            metadata
              .packageServiceName ||
            'Package'
          )
        : 'Courtesy / Free';

    const included =
      item.included !== false;

    const dateDisabled =
      !editable ||
      Boolean(fixedDate);

    return `
      <article
        class="aa-task-generator-task
          ${included ? '' : 'is-excluded'}"
        data-aa-generated-task="${this.escapeHtml(
          item.id
        )}"
      >
        <div class="aa-task-generator-task__top">
          <label class="aa-task-generator-task__include">
            <input
              type="checkbox"
              data-aa-generated-field="included"
              ${included ? 'checked' : ''}
              ${editable ? '' : 'disabled'}
            >
            <span>
              Include
            </span>
          </label>

          <div class="aa-task-generator-task__badges">
            <span>
              ${this.escapeHtml(
                this.getSlotLabel(item)
              )}
            </span>
            <span>
              ${this.escapeHtml(
                packageLabel
              )}
            </span>
          </div>
        </div>

        <div class="aa-task-generator-task__grid">
          <label>
            <span>Date</span>
            <input
              type="date"
              value="${this.escapeHtml(
                item.due_date ||
                fixedDate ||
                ''
              )}"
              ${allowedMin
                ? `min="${this.escapeHtml(allowedMin)}"`
                : ''
              }
              ${allowedMax
                ? `max="${this.escapeHtml(allowedMax)}"`
                : ''
              }
              data-aa-generated-field="due_date"
              ${dateDisabled ? 'disabled' : ''}
            >
          </label>

          <label class="aa-task-generator-task__item">
            <span>
              Specific Item
            </span>
            <input
              type="text"
              value="${this.escapeHtml(
                item.specific_item || ''
              )}"
              data-aa-generated-field="specific_item"
              ${editable ? '' : 'disabled'}
            >
          </label>

          <label class="aa-task-generator-task__notes">
            <span>Notes</span>
            <textarea
              rows="3"
              data-aa-generated-field="notes"
              ${editable ? '' : 'disabled'}
            >${this.escapeHtml(
              item.notes || ''
            )}</textarea>
          </label>
        </div>

        ${item.reason
          ? `
            <p class="aa-task-generator-task__reason">
              <strong>Why:</strong>
              ${this.escapeHtml(
                item.reason
              )}
            </p>
          `
          : ''
        }
      </article>
    `;
  }

  renderAudit(batch) {
    const issues =
      Array.isArray(
        batch.catalog_issues
      )
        ? batch.catalog_issues
        : [];

    const concerns =
      Array.isArray(
        batch.capacity_concerns
      )
        ? batch.capacity_concerns
        : [];

    const audit =
      Array.isArray(
        batch.coverage_audit
      )
        ? batch.coverage_audit
        : [];

    return `
      <div class="aa-task-generator-audit-grid">
        <section
          class="aa-task-generator-audit-card
            ${issues.length ? 'needs-attention' : ''}"
        >
          <div>
            <span>Catalog Issues</span>
            <strong>
              ${issues.length
                ? `${issues.length} need attention`
                : 'None found'
              }
            </strong>
          </div>

          ${issues.length
            ? `
              <ul>
                ${issues
                  .map(
                    (issue) =>
                      `<li>${this.escapeHtml(issue)}</li>`
                  )
                  .join('')}
              </ul>
            `
            : ''
          }
        </section>

        <section
          class="aa-task-generator-audit-card
            ${concerns.length ? 'needs-attention' : ''}"
        >
          <div>
            <span>Capacity / Schedule</span>
            <strong>
              ${concerns.length
                ? `${concerns.length} note${concerns.length === 1 ? '' : 's'}`
                : 'No concerns'
              }
            </strong>
          </div>

          ${concerns.length
            ? `
              <ul>
                ${concerns
                  .map(
                    (concern) =>
                      `<li>${this.escapeHtml(concern)}</li>`
                  )
                  .join('')}
              </ul>
            `
            : ''
          }
        </section>
      </div>

      <details class="aa-task-generator-coverage">
        <summary>
          <span>Coverage Check</span>
          <strong>
            ${audit.length}
            catalog item${audit.length === 1 ? '' : 's'} reviewed
          </strong>
        </summary>

        <div class="aa-task-generator-coverage__rows">
          ${audit.length
            ? audit
                .map(
                  (entry) => `
                    <div>
                      <strong>
                        ${this.escapeHtml(
                          entry.item ||
                          'Catalog item'
                        )}
                      </strong>
                      <span>
                        ${this.escapeHtml(
                          entry.disposition ||
                          ''
                        )}
                      </span>
                      <p>
                        ${this.escapeHtml(
                          entry.detail ||
                          ''
                        )}
                      </p>
                    </div>
                  `
                )
                .join('')
            : `
                <p>
                  No coverage audit was returned.
                </p>
              `
          }
        </div>
      </details>
    `;
  }

  renderCurrentPanel() {
    const host =
      this.getPanelHost();

    if (
      !host ||
      !this.currentCatalog ||
      !this.currentBatch
    ) {
      return;
    }

    const batch =
      this.currentBatch;

    const editable =
      batch.status === 'draft';

    const items =
      Array.isArray(batch.items)
        ? batch.items
        : [];

    const grouped =
      items.reduce(
        (map, item) => {
          const key =
            item.task_type ||
            'Other';

          if (!map[key]) {
            map[key] = [];
          }

          map[key].push(item);

          return map;
        },
        {}
      );

    const typeOrder = [
      'Email',
      'Social Post',
      'Website Banner',
      'Web Sales Promotion',
      'Manufacturer Promotion',
      'Sales Page',
      'Website Update',
      'Catalog / Flipbook',
      'Reminder',
      'Invoice',
      'Admin'
    ];

    const groupNames =
      Object.keys(grouped)
        .sort(
          (a, b) => {
            const ai =
              typeOrder.indexOf(a);

            const bi =
              typeOrder.indexOf(b);

            if (
              ai !== -1 ||
              bi !== -1
            ) {
              return (
                (ai === -1 ? 999 : ai) -
                (bi === -1 ? 999 : bi)
              );
            }

            return a.localeCompare(b);
          }
        );

    const statusLabels = {
      generating: 'Leo is working',
      draft: 'Draft Proposal',
      approved: 'Approved · In My Work',
      undone: 'Undone',
      failed: 'Needs Attention'
    };

    if (
      batch.status ===
      'generating'
    ) {
      host.innerHTML = `
        <section class="aa-task-generator-panel">
          <div class="aa-task-generator-panel__header">
            <div>
              <p class="aa-card__label">
                Leo
              </p>
              <h3>
                Leo is building
                ${this.escapeHtml(
                  this.formatMonth(
                    this.currentCatalog.month
                  )
                )}
                for
                ${this.escapeHtml(
                  this.currentCatalog.clientName
                )}
              </h3>
              <p>
                ${this.escapeHtml(
                  this.currentCatalog.title
                )}
              </p>
            </div>

            <div class="aa-task-generator-panel__header-actions">
              <span
                class="aa-task-generator-batch-status is-generating"
              >
                Leo is working
              </span>

              <button
                type="button"
                class="aa-button"
                data-aa-close-task-generator
              >
                Close
              </button>
            </div>
          </div>

          <div class="aa-task-generator-working">
            <strong>
              Reading the catalog and planning the full month.
            </strong>
            <p>
              You can leave this page. Leo keeps working in the background.
              When you come back, opening Generate Tasks reconnects to this same run.
            </p>
          </div>

          <p
            class="aa-task-generator-feedback"
            data-aa-task-generator-feedback
          ></p>
        </section>
      `;

      return;
    }

    host.innerHTML = `
      <section class="aa-task-generator-panel">
        <div class="aa-task-generator-panel__header">
          <div>
            <p class="aa-card__label">
              Task Generator
            </p>
            <h3>
              ${this.escapeHtml(
                this.currentCatalog.clientName
              )}
              ·
              ${this.escapeHtml(
                this.formatMonth(
                  this.currentCatalog.month
                )
              )}
            </h3>
            <p>
              ${this.escapeHtml(
                this.currentCatalog.title
              )}
            </p>
          </div>

          <div class="aa-task-generator-panel__header-actions">
            <span
              class="aa-task-generator-batch-status
                is-${this.escapeHtml(batch.status)}"
            >
              ${this.escapeHtml(
                statusLabels[batch.status] ||
                batch.status
              )}
            </span>

            <button
              type="button"
              class="aa-button"
              data-aa-close-task-generator
            >
              Close
            </button>
          </div>
        </div>

        ${batch.summary
          ? `
            <div class="aa-task-generator-summary">
              <strong>Plan Summary</strong>
              <p>
                ${this.escapeHtml(
                  batch.summary
                )}
              </p>
            </div>
          `
          : ''
        }

        <label class="aa-task-generator-monthly-notes">
          <span>
            Monthly Notes
          </span>

          <textarea
            rows="6"
            data-aa-task-generator-monthly-notes
            ${editable ? '' : 'disabled'}
          >${this.escapeHtml(
            batch.monthly_notes || ''
          )}</textarea>
        </label>

        ${this.renderAudit(batch)}

        <div class="aa-task-generator-plan">
          <div class="aa-task-generator-plan__header">
            <div>
              <span>Proposed Tasks</span>
              <strong>
                ${items.filter(
                  (item) =>
                    item.included !== false
                ).length}
                included
              </strong>
            </div>

            ${editable
              ? `
                <p>
                  Edit the content or flexible dates here.
                  Nothing reaches My Work until you approve it.
                </p>
              `
              : ''
            }
          </div>

          ${groupNames
            .map(
              (taskType) =>
                this.renderTaskGroup(
                  taskType,
                  grouped[taskType],
                  editable
                )
            )
            .join('')}
        </div>

        <div class="aa-task-generator-panel__actions">
          ${batch.status === 'draft'
            ? `
              <button
                type="button"
                class="aa-button"
                data-aa-save-generated-tasks
              >
                Save Changes
              </button>

              <button
                type="button"
                class="aa-button"
                data-aa-regenerate-tasks
              >
                Regenerate Proposal
              </button>

              <button
                type="button"
                class="aa-button aa-button--primary"
                data-aa-approve-generated-tasks
              >
                Approve to My Work
              </button>
            `
            : ''
          }

          ${batch.status === 'approved'
            ? `
              <button
                type="button"
                class="aa-button aa-button--danger"
                data-aa-undo-generated-tasks
              >
                Undo Generated Tasks
              </button>
            `
            : ''
          }

          ${batch.status === 'undone'
            ? `
              <button
                type="button"
                class="aa-button aa-button--primary"
                data-aa-regenerate-tasks
              >
                Generate New Proposal
              </button>
            `
            : ''
          }
        </div>

        <p
          class="aa-task-generator-feedback"
          data-aa-task-generator-feedback
        ></p>
      </section>
    `;
  }

  setFeedback(
    message,
    tone = ''
  ) {
    const el =
      this.root.querySelector(
        '[data-aa-task-generator-feedback]'
      );

    if (!el) {
      return;
    }

    el.textContent =
      message || '';

    el.className =
      'aa-task-generator-feedback';

    if (tone) {
      el.classList.add(
        `is-${tone}`
      );
    }
  }

  setButtonBusy(
    button,
    busyText
  ) {
    if (!button) {
      return () => {};
    }

    const original =
      button.textContent;

    button.disabled = true;
    button.textContent =
      busyText;

    return () => {
      button.disabled = false;
      button.textContent =
        original;
    };
  }

  async runGeneration(button) {
    if (!this.currentCatalog) {
      return;
    }

    const notes =
      this.getPanelHost()
        ?.querySelector(
          '[data-aa-task-generator-monthly-notes]'
        )
        ?.value ||
      '';

    const restore =
      this.setButtonBusy(
        button,
        'Building Proposal...'
      );

    this.setFeedback(
      'Reading the catalog and building the full month. This can take a little bit.'
    );

    try {
      const batch =
        await window.AASupabaseClient
          .invokeTaskGenerator({
            catalogId:
              this.currentCatalog.id,
            monthlyNotes: notes
          });

      if (!batch?.id) {
        throw new Error(
          'No proposal was returned.'
        );
      }

      this.currentBatch =
        await window.AASupabaseClient
          .getTaskGenerationBatch({
            id: batch.id
          });

      this.renderCurrentPanel();
      await this
        .refreshCatalogStatuses();

      this.setFeedback(
        'Proposal ready for review.',
        'success'
      );
    } catch (error) {
      console.error(
        'Task generation failed:',
        error
      );

      this.setFeedback(
        error?.message ||
        'Unable to generate the proposal.',
        'error'
      );
    } finally {
      restore();
    }
  }

  async saveCurrentBatch({
    quiet = false
  } = {}) {
    if (
      !this.currentBatch ||
      this.currentBatch.status !==
        'draft'
    ) {
      return;
    }

    const rows =
      Array.from(
        this.getPanelHost()
          ?.querySelectorAll(
            '[data-aa-generated-task]'
          ) || []
      );

    const monthlyNotes =
      String(
        this.getPanelHost()
          ?.querySelector(
            '[data-aa-task-generator-monthly-notes]'
          )
          ?.value ||
        ''
      );

    await Promise.all([
      window.AASupabaseClient
        .updateTaskGenerationBatch({
          id:
            this.currentBatch.id,
          updates: {
            monthlyNotes
          }
        }),
      ...rows.map(async (row) => {
        const id =
          row.dataset
            .aaGeneratedTask;

        const getField =
          (name) =>
            row.querySelector(
              `[data-aa-generated-field="${name}"]`
            );

        const included =
          Boolean(
            getField('included')
              ?.checked
          );

        const dueField =
          getField('due_date');

        const specificItem =
          String(
            getField('specific_item')
              ?.value || ''
          ).trim();

        const notes =
          String(
            getField('notes')
              ?.value || ''
          ).trim();

        await window.AASupabaseClient
          .updateTaskGenerationItem({
            id,
            updates: {
              included,
              dueDate:
                included
                  ? (
                      dueField?.value ||
                      null
                    )
                  : null,
              specificItem:
                included
                  ? specificItem
                  : '',
              notes:
                included
                  ? notes
                  : null
            }
          });
      })
    ]);

    this.currentBatch =
      await window.AASupabaseClient
        .getTaskGenerationBatch({
          id:
            this.currentBatch.id
        });

    if (!quiet) {
      this.renderCurrentPanel();
      this.setFeedback(
        'Changes saved.',
        'success'
      );
    }
  }

  async approveCurrentBatch(
    button
  ) {
    if (
      !this.currentBatch ||
      this.currentBatch.status !==
        'draft'
    ) {
      return;
    }

    const restore =
      this.setButtonBusy(
        button,
        'Approving...'
      );

    try {
      await this.saveCurrentBatch({
        quiet: true
      });

      const count =
        await window.AASupabaseClient
          .approveTaskGenerationBatch({
            batchId:
              this.currentBatch.id
          });

      this.currentBatch =
        await window.AASupabaseClient
          .getTaskGenerationBatch({
            id:
              this.currentBatch.id
          });

      this.renderCurrentPanel();
      await this
        .refreshCatalogStatuses();

      this.setFeedback(
        `${count} task${count === 1 ? '' : 's'} added to My Work.`,
        'success'
      );
    } catch (error) {
      console.error(
        'Unable to approve generated tasks:',
        error
      );

      this.setFeedback(
        error?.message ||
        'Unable to approve the generated tasks.',
        'error'
      );
    } finally {
      restore();
    }
  }

  async undoCurrentBatch(
    button
  ) {
    if (
      !this.currentBatch ||
      this.currentBatch.status !==
        'approved'
    ) {
      return;
    }

    if (
      this.undoConfirmBatchId !==
      this.currentBatch.id
    ) {
      this.undoConfirmBatchId =
        this.currentBatch.id;

      button.textContent =
        'Click Again to Confirm Undo';

      this.setFeedback(
        'This removes only the tasks created by this generation batch.'
      );

      return;
    }

    const restore =
      this.setButtonBusy(
        button,
        'Undoing...'
      );

    try {
      const count =
        await window.AASupabaseClient
          .undoTaskGenerationBatch({
            batchId:
              this.currentBatch.id
          });

      this.undoConfirmBatchId =
        null;

      this.currentBatch =
        await window.AASupabaseClient
          .getTaskGenerationBatch({
            id:
              this.currentBatch.id
          });

      this.renderCurrentPanel();
      await this
        .refreshCatalogStatuses();

      this.setFeedback(
        `${count} generated task${count === 1 ? '' : 's'} removed from My Work.`,
        'success'
      );
    } catch (error) {
      console.error(
        'Unable to undo generated tasks:',
        error
      );

      this.undoConfirmBatchId =
        null;

      this.setFeedback(
        error?.message ||
        'Unable to undo this generated batch.',
        'error'
      );
    } finally {
      restore();
    }
  }

  async saveSettings(form) {
    const textarea =
      form.querySelector(
        '[data-aa-task-generator-instructions]'
      );

    const feedback =
      form.querySelector(
        '[data-aa-task-generator-settings-feedback]'
      );

    const button =
      form.querySelector(
        'button[type="submit"]'
      );

    const instructions =
      String(
        textarea?.value || ''
      ).trim();

    if (!instructions) {
      if (feedback) {
        feedback.textContent =
          'Instructions cannot be empty.';
        feedback.className =
          'aa-task-generator-feedback is-error';
      }

      return;
    }

    const restore =
      this.setButtonBusy(
        button,
        'Saving...'
      );

    try {
      await window.AASupabaseClient
        .saveTaskGeneratorSettings({
          instructions
        });

      if (feedback) {
        feedback.textContent =
          'Generator instructions saved.';
        feedback.className =
          'aa-task-generator-feedback is-success';
      }
    } catch (error) {
      console.error(
        'Unable to save Task Generator settings:',
        error
      );

      if (feedback) {
        feedback.textContent =
          error?.message ||
          'Unable to save generator instructions.';
        feedback.className =
          'aa-task-generator-feedback is-error';
      }
    } finally {
      restore();
    }
  }

  bindEvents() {
    this.root.addEventListener(
      'click',
      async (event) => {
        const generateButton =
          event.target.closest(
            '[data-aa-generate-catalog-tasks]'
          );

        if (generateButton) {
          await this
            .openCatalogGenerator(
              generateButton
            );
          return;
        }

        if (
          event.target.closest(
            '[data-aa-close-task-generator]'
          )
        ) {
          this.generationPollToken += 1;

          const host =
            this.getPanelHost();

          if (host) {
            host.innerHTML = '';
          }

          this.currentCatalog = null;
          this.currentBatch = null;
          this.undoConfirmBatchId =
            null;
          return;
        }

        const runButton =
          event.target.closest(
            '[data-aa-run-task-generator]'
          );

        if (runButton) {
          await this.runGeneration(
            runButton
          );
          return;
        }

        const saveButton =
          event.target.closest(
            '[data-aa-save-generated-tasks]'
          );

        if (saveButton) {
          const restore =
            this.setButtonBusy(
              saveButton,
              'Saving...'
            );

          try {
            await this
              .saveCurrentBatch();
          } catch (error) {
            console.error(
              'Unable to save generated task edits:',
              error
            );

            this.setFeedback(
              error?.message ||
              'Unable to save changes.',
              'error'
            );
          } finally {
            restore();
          }

          return;
        }

        const regenerateButton =
          event.target.closest(
            '[data-aa-regenerate-tasks]'
          );

        if (regenerateButton) {
          await this.runGeneration(
            regenerateButton
          );
          return;
        }

        const approveButton =
          event.target.closest(
            '[data-aa-approve-generated-tasks]'
          );

        if (approveButton) {
          await this
            .approveCurrentBatch(
              approveButton
            );
          return;
        }

        const undoButton =
          event.target.closest(
            '[data-aa-undo-generated-tasks]'
          );

        if (undoButton) {
          await this
            .undoCurrentBatch(
              undoButton
            );
        }
      }
    );

    this.root.addEventListener(
      'submit',
      async (event) => {
        const form =
          event.target.closest(
            '[data-aa-task-generator-settings-form]'
          );

        if (!form) {
          return;
        }

        event.preventDefault();
        await this.saveSettings(form);
      }
    );
  }
}

window.AATaskGeneratorWorkspace =
  AATaskGeneratorWorkspace;
